import './styles.scss'

import { useCallback, useRef } from 'react'
import { Carousel, Grid } from 'antd'

import { useAppDispatch } from '@/store/hook'
import { addProductToCart } from '@/store/slices/cartSlice'
import { sendProductDetail, showAddProductToWishlistModal } from '@/store/slices/wishlistSlice'
import usePermission from '@/hooks/usePermission'
import { ALL_SEARCH_RESULTS_PER_SECTION } from '@/libs/constants'
import { MODULE_SHOP_NAVIGATION_RIGHTS } from '@/libs/constants/modulerights'

import AddToCartBtn from '../AddToCartBtn'
import B2becPagination from '../B2becPagination/B2becPagination'
import ProductCard from '../ProductCard'

const mergeArray = (length = 0) => {
  return (items = []) => {
    if (items.length < length) {
      const listItemsPared = Array.from({ length })
        .fill({
          materialNumber: new Date().toDateString(),
          isEmpty: true,
        })
        .reduce((pre, c, index) => {
          const result = pre
          const data = items[index]
          result[index] = data || c

          return result
        }, [])
      return listItemsPared
    }

    return items
  }
}

const { useBreakpoint } = Grid

interface B2BProductCarouselProps {
  listItems: {
    date?: string
    name?: string
    img?: string
    category?: string
    materialNumber?: string
    serialNo?: string
    url?: string
  }[]
  productLimitPerSection?: number
  productPerPage?: number
  className?: string
  isCompareCheckboxDisplayed?: boolean
  onCheckHandler?: (...args: unknown[]) => unknown
  comparingProductIds?: string[]
}

const B2BProductCarousel = (props: B2BProductCarouselProps) => {
  const carouselElement = useRef(null)
  const dispatch = useAppDispatch()
  const breakpoint = useBreakpoint()
  const {
    listItems,
    className,
    productLimitPerSection = 6,
    productPerPage = 3,
    isCompareCheckboxDisplayed = false,
    onCheckHandler,
    comparingProductIds,
  } = props

  const { hasPermission: hasViewListPricePermission } = usePermission(
    MODULE_SHOP_NAVIGATION_RIGHTS.VIEW_LIST_PRICE,
  )

  const settings = () => {
    if (breakpoint.xl) {
      return { slidesToShow: productPerPage, slidesToScroll: productPerPage }
    }

    if (breakpoint.md) {
      return { slidesToShow: 2, slidesToScroll: 2 }
    }

    return {
      slidesToShow: 1,
      slidesToScroll: 1,
    }
  }

  const onAddProductToCart = useCallback(
    (materialId) => {
      dispatch(addProductToCart({ materialNumber: materialId }))
    },
    [dispatch],
  )

  const handleAddToWishlist = ({ materialName, materialNumber, img }) => {
    dispatch(
      sendProductDetail({
        materialName,
        materialNumber,
        img,
      }),
    )
    dispatch(showAddProductToWishlistModal())
  }

  const configs = settings()

  const listItemsMapFunction = mergeArray(configs.slidesToShow)

  return (
    <div className={`b2b-carousel ${className}`}>
      <Carousel {...settings()} ref={carouselElement} dots={false}>
        {listItemsMapFunction(listItems)
          .slice(0, ALL_SEARCH_RESULTS_PER_SECTION)
          .map((item) => (
            <div
              key={`${item?.materialNumber}-${item?.name}-${item?.category}`}
              className="b2b-carousel__item"
            >
              {item.isEmpty ? null : (
                <ProductCard
                  className="--no-shadow"
                  {...item}
                  comparingProductIds={comparingProductIds}
                  currencyUnit={item?.currencyUnit || item?.currency}
                  isCompareCheckboxDisplayed={isCompareCheckboxDisplayed}
                  listPrice={item?.listPrice || item?.price}
                  requiresInstruction={item.requiresInstruction}
                  addToCartBtn={
                    <AddToCartBtn
                      handleClick={() => onAddProductToCart(item?.materialNumber)}
                    />
                  }
                  shouldDisplayListPrice={item?.displayListPrice && hasViewListPricePermission}
                  onCheckHandler={onCheckHandler}
                  onAddToWishlist={() =>
                    handleAddToWishlist({
                      materialName: item?.name,
                      materialNumber: item?.materialId || item?.materialNumber,
                      img: item?.img || item?.imageUrl,
                    })}
                />
              )}
            </div>
          ))}
      </Carousel>
      <div className="b2b-carousel__dots mt-4">
        <B2becPagination
          pageSize={configs.slidesToShow}
          size="small"
          total={productLimitPerSection}
          onPageChange={(page, pageSize) => {
            const offset = page * pageSize - pageSize

            carouselElement.current.goTo(offset)
          }}
        />
      </div>
    </div>
  )
}

export default B2BProductCarousel
